import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DropdownArrow from '../components/DropdownArrow';
import { Container, Table, Button, Modal, Form, Alert, Pagination, Card, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { paymentService, accountHolderService, trustAccountService } from '../services/apiClient';
import ConfirmationModal from '../components/ConfirmationModal';
import LoadingIndicator from '../components/LoadingIndicator';
import { Statements } from './Statements';
import { Payment, AccountHolder, CreatePaymentRequest, TrustAccount } from '../types/index';
import { useLocalization } from '../context/LocalizationContext';
import { buildSystemCategoryKeywordConfig, resolveTransactionSystemCategory } from '../utils/systemCategoryMapping';
import { getActiveSessionMonth, isSameMonth, setActiveSessionMonth, subscribeToActiveSessionMonth } from '../utils/sessionMonth';
import { getSavedTrustAccountId, isSavingsTrustAccount, saveSelectedTrustAccountId, sortTrustAccountsByKind, TRUST_ACCOUNT_KIND_KEY } from '../utils/trustAccount';
import './Payments.css';

interface AccountHolderSummaryRow {
  accountHolderId: number;
  accountHolderName: string;
  transactionCount: number;
  totalAmount: number;
  holderCredits: number;
  monthlyAccountFees: number;
  outstandingFees: number;
}

interface BankFeeChargeSummaryRow {
  categoryName: string;
  transactionCount: number;
  totalAmount: number;
}

export const Payments: React.FC = () => {
  const { t, formatCurrency, formatDate, formatMonthYear, language } = useLocalization();
  const tRef = useRef(t);
  const initialSelectedMonthRef = useRef<Date>(
    getActiveSessionMonth(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1))
  );
  const getDefaultPaymentDate = () => new Date().toISOString().split('T')[0];

  const readSelectedTrustAccountKind = () => (localStorage.getItem(TRUST_ACCOUNT_KIND_KEY) || '').trim();
  const readSelectedTrustAccountId = () => getSavedTrustAccountId() || undefined;

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const getPreviousMonth = (): Date => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  };

  const [payments, setPayments] = useState<Payment[]>([]);
  const [lastThreeMonthsPayments, setLastThreeMonthsPayments] = useState<Payment[]>([]);
  const [trustAccounts, setTrustAccounts] = useState<TrustAccount[]>([]);
  const [selectedTrustAccountId, setSelectedTrustAccountId] = useState<number | undefined>(readSelectedTrustAccountId);
  const [selectedTrustAccountKind, setSelectedTrustAccountKind] = useState<string>(readSelectedTrustAccountKind);
  const [accountHolders, setAccountHolders] = useState<AccountHolder[]>([]);
  const [automapperKeywordHolders, setAutomapperKeywordHolders] = useState<AccountHolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTrustAccounts, setLoadingTrustAccounts] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showStatementsCenterModal, setShowStatementsCenterModal] = useState(false);
  const [showAccountSwitchModal, setShowAccountSwitchModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [filterAccountId, setFilterAccountId] = useState<number | undefined>();
  const [selectedMonth, setSelectedMonth] = useState<Date>(initialSelectedMonthRef.current);
  const [debouncedSelectedMonth, setDebouncedSelectedMonth] = useState<Date>(initialSelectedMonthRef.current);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [feedback, setFeedback] = useState<{ variant: 'success' | 'danger' | 'info'; message: string } | null>(null);
  const [modalFeedback, setModalFeedback] = useState<{ variant: 'success' | 'danger' | 'info'; message: string } | null>(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentFormValidated, setPaymentFormValidated] = useState(false);
  const [draftPaymentsToAdd, setDraftPaymentsToAdd] = useState<CreatePaymentRequest[]>([]);
  const [modalDraftPage, setModalDraftPage] = useState(1);
  const [modalDraftPageSize, setModalDraftPageSize] = useState(10);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [formData, setFormData] = useState<CreatePaymentRequest>({
    accountHolderId: 0,
    trustAccountId: readSelectedTrustAccountId() || 1,
    amount: 0,
    paymentDate: getDefaultPaymentDate(),
    description: '',
  });

  const normalizedSelectedTrustAccountKind = selectedTrustAccountKind.toLowerCase();
  const isSavingsMode = normalizedSelectedTrustAccountKind === 'savings';

  // Confirmation modal state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    cancelText?: string;
  } | null>(null);

  useEffect(() => {
    return subscribeToActiveSessionMonth((nextMonth) => {
      setSelectedMonth((previousMonth) => (isSameMonth(previousMonth, nextMonth) ? previousMonth : nextMonth));
    });
  }, []);

  useEffect(() => {
    setActiveSessionMonth(selectedMonth);
  }, [selectedMonth]);

  const toMonthKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const getMonthRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    return {
      start: new Date(`${start.toISOString().split('T')[0]}T00:00:00`).toISOString(),
      end: new Date(`${end.toISOString().split('T')[0]}T23:59:59`).toISOString(),
    };
  };

  const getThreeMonthRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth() - 2, 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    return {
      start: new Date(`${start.toISOString().split('T')[0]}T00:00:00`).toISOString(),
      end: new Date(`${end.toISOString().split('T')[0]}T23:59:59`).toISOString(),
    };
  };

  const systemCategoryKeywordConfig = useMemo(
    () => buildSystemCategoryKeywordConfig(automapperKeywordHolders),
    [automapperKeywordHolders]
  );

  const selectedTrustAccount = useMemo(
    () => trustAccounts.find((account) => account.id === selectedTrustAccountId) || null,
    [selectedTrustAccountId, trustAccounts]
  );
  const isCurrentAccountMode = !isSavingsTrustAccount(selectedTrustAccount);

  const selectedTrustAccountTypeLabel = useMemo(
    () => ((selectedTrustAccount?.accountKind || '').trim().toLowerCase() === 'savings' ? t('savingsAccountType') : t('currentAccountType')),
    [selectedTrustAccount?.accountKind, t]
  );

  const loadAccountHolders = useCallback(async () => {
    try {
      const [holdersData, holdersWithSystemData] = await Promise.all([
        accountHolderService.getAll(),
        accountHolderService.getAll(true),
      ]);
      setAccountHolders(holdersData);
      setAutomapperKeywordHolders(holdersWithSystemData);
    } catch (err) {
      const failureMessage = tRef.current('failedToLoadData');
      setError(failureMessage);
      setFeedback({ variant: 'danger', message: failureMessage });
      console.error(err);
    }
  }, []);

  const loadTrustAccounts = useCallback(async () => {
    try {
      setLoadingTrustAccounts(true);
      const trustAccountsData = sortTrustAccountsByKind(await trustAccountService.getAll());
      setTrustAccounts(trustAccountsData);

      const savedTrustAccountId = readSelectedTrustAccountId();
      const activeTrustAccount = (savedTrustAccountId
        ? trustAccountsData.find((account) => account.id === savedTrustAccountId)
        : null) ?? trustAccountsData[0] ?? null;

      if (!activeTrustAccount) {
        setSelectedTrustAccountId(undefined);
        setSelectedTrustAccountKind('');
        return;
      }

      setSelectedTrustAccountId(activeTrustAccount.id);
      setSelectedTrustAccountKind((activeTrustAccount.accountKind || '').trim());
      saveSelectedTrustAccountId(activeTrustAccount.id, activeTrustAccount.accountKind);
      setFormData((prev) => (
        prev.trustAccountId === activeTrustAccount.id
          ? prev
          : { ...prev, trustAccountId: activeTrustAccount.id }
      ));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTrustAccounts(false);
    }
  }, []);

  const loadSelectedMonthPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const selectedMonthRange = getMonthRange(debouncedSelectedMonth);
      const paymentsData = selectedTrustAccountId
        ? await paymentService.getTrustTransactions(selectedTrustAccountId, selectedMonthRange.start, selectedMonthRange.end)
        : await paymentService.getAll(filterAccountId, selectedMonthRange.start, selectedMonthRange.end);

      const sortedMonthPayments = [...paymentsData].sort(
        (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
      );

      setPayments(sortedMonthPayments);
    } catch (err) {
      const failureMessage = tRef.current('failedToLoadData');
      setError(failureMessage);
      setFeedback({ variant: 'danger', message: failureMessage });
      console.error(err);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [debouncedSelectedMonth, filterAccountId, selectedTrustAccountId]);

  const loadThreeMonthOverview = useCallback(async () => {
    try {
      const threeMonthRange = getThreeMonthRange(debouncedSelectedMonth);
      const threeMonthPaymentsData = selectedTrustAccountId
        ? await paymentService.getTrustTransactions(selectedTrustAccountId, threeMonthRange.start, threeMonthRange.end)
        : await paymentService.getAll(undefined, threeMonthRange.start, threeMonthRange.end);
      setLastThreeMonthsPayments(threeMonthPaymentsData);
    } catch (err) {
      console.error(err);
    }
  }, [debouncedSelectedMonth, selectedTrustAccountId]);

  useEffect(() => {
    const syncSelectedTrustAccount = () => {
      setSelectedTrustAccountId(readSelectedTrustAccountId());
      setSelectedTrustAccountKind(readSelectedTrustAccountKind());
    };

    window.addEventListener('trust-account-changed', syncSelectedTrustAccount);
    window.addEventListener('storage', syncSelectedTrustAccount);

    return () => {
      window.removeEventListener('trust-account-changed', syncSelectedTrustAccount);
      window.removeEventListener('storage', syncSelectedTrustAccount);
    };
  }, []);

  useEffect(() => {
    if (isSavingsMode) {
      setFilterAccountId(undefined);
    }
  }, [isSavingsMode]);

  useEffect(() => {
    void loadAccountHolders();
  }, [loadAccountHolders]);

  useEffect(() => {
    void loadTrustAccounts();
  }, [loadTrustAccounts]);

  useEffect(() => {
    void loadSelectedMonthPayments();
  }, [loadSelectedMonthPayments]);

  useEffect(() => {
    void loadThreeMonthOverview();
  }, [loadThreeMonthOverview]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSelectedMonth((previousMonth) => {
        if (
          previousMonth.getFullYear() === selectedMonth.getFullYear()
          && previousMonth.getMonth() === selectedMonth.getMonth()
        ) {
          return previousMonth;
        }

        return selectedMonth;
      });
    }, 150);

    return () => clearTimeout(debounceTimer);
  }, [selectedMonth]);

  const handleSwitchAccount = (account: TrustAccount) => {
    setShowAccountSwitchModal(false);

    if (account.id === selectedTrustAccountId) {
      return;
    }

    saveSelectedTrustAccountId(account.id, account.accountKind);
    setFormData((prev) => ({
      ...prev,
      trustAccountId: account.id,
    }));
  };

  const handleShowModal = (payment?: Payment) => {
    setFeedback(null);
    setModalFeedback(null);
    setPaymentFormValidated(false);

    if (payment) {
      setEditingId(payment.id);
      setDraftPaymentsToAdd([]);
      setFormData({
        accountHolderId: payment.accountHolderId,
        trustAccountId: payment.trustAccountId,
        amount: payment.amount,
        paymentDate: payment.paymentDate.split('T')[0],
        description: payment.description,
      });
    } else {
      setEditingId(null);
      setDraftPaymentsToAdd([]);
      setFormData({
        accountHolderId: 0,
        trustAccountId: selectedTrustAccountId || 1,
        amount: 0,
        paymentDate: getDefaultPaymentDate(),
        description: '',
      });
    }
    setShowModal(true);
  };

  const hasPendingAddPaymentChanges = () => {
    if (editingId) {
      return false;
    }

    if (draftPaymentsToAdd.length > 0) {
      return true;
    }

    return (
      formData.accountHolderId > 0
      || formData.amount !== 0
      || formData.description.trim().length > 0
      || formData.paymentDate !== getDefaultPaymentDate()
    );
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setPaymentFormValidated(false);
    setDraftPaymentsToAdd([]);
    setModalFeedback(null);
  };

  const handleRequestCloseModal = () => {
    if (savingPayment) {
      return;
    }

    if (hasPendingAddPaymentChanges()) {
      setConfirmationConfig({
        title: t('unsavedChanges'),
        message: t('cancelAddPaymentConfirmMessage'),
        variant: 'success',
        confirmText: t('discardChanges'),
        cancelText: t('keepEditing'),
        onConfirm: () => {
          setShowConfirmation(false);
          setConfirmationConfig(null);
          handleCloseModal();
        },
      });
      setShowConfirmation(true);
      return;
    }

    handleCloseModal();
  };

  const handlePaymentDateChange = (date: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      paymentDate: date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        : '',
    }));
  };

  const parseLocalDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'amount' || name === 'accountHolderId' || name === 'trustAccountId'
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const isPaymentFormValid = (): boolean => {
    return (
      formData.accountHolderId > 0
      && formData.amount !== 0
      && formData.paymentDate.trim().length > 0
      && formData.description.trim().length > 0
    );
  };

  const buildPaymentPayloadFromForm = (): CreatePaymentRequest => ({
    ...formData,
    paymentDate: formData.paymentDate.trim(),
    description: formData.description.trim(),
  });

  const resetAddPaymentForm = () => {
    setFormData((prev) => ({
      ...prev,
      accountHolderId: 0,
      amount: 0,
      description: '',
    }));
    setPaymentFormValidated(false);
  };

  const handleAddDraftPayment = () => {
    setPaymentFormValidated(true);
    setModalFeedback(null);

    if (formData.accountHolderId <= 0) {
      return;
    }

    if (formData.amount === 0) {
      setModalFeedback({ variant: 'danger', message: t('paymentAmountMustBeGreaterThanZero') });
      return;
    }

    if (!formData.paymentDate.trim() || !formData.description.trim()) {
      setModalFeedback({ variant: 'danger', message: t('pleaseCompleteRequiredFields') });
      return;
    }

    setDraftPaymentsToAdd((prev) => {
      const next = [...prev, buildPaymentPayloadFromForm()];
      setModalDraftPage(Math.ceil(next.length / modalDraftPageSize));
      return next;
    });
    resetAddPaymentForm();
  };

  const handleDraftPaymentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDraftPayment();
    }
  };

  const handleRemoveDraftPayment = (idx: number) => {
    setDraftPaymentsToAdd((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      const maxPage = Math.max(1, Math.ceil(next.length / modalDraftPageSize));
      setModalDraftPage((p) => Math.min(p, maxPage));
      return next;
    });
  };

  const handleClearDraftPayments = () => {
    setDraftPaymentsToAdd([]);
    setModalDraftPage(1);
    resetAddPaymentForm();
    setModalFeedback(null);
  };

  const isBatchAccountHolderInvalid = paymentFormValidated && formData.accountHolderId <= 0;
  const isBatchAmountInvalid = paymentFormValidated && formData.amount === 0;
  const isBatchPaymentDateInvalid = paymentFormValidated && formData.paymentDate.trim().length === 0;
  const isBatchDescriptionInvalid = paymentFormValidated && formData.description.trim().length === 0;

  const handleSavePayment = async () => {
    setPaymentFormValidated(true);

    try {
      setSavingPayment(true);
      setFeedback({ variant: 'info', message: editingId ? t('savingPaymentChanges') : t('creatingPayment') });

      if (editingId) {
        if (!isPaymentFormValid()) {
          setFeedback({ variant: 'danger', message: t('pleaseCompleteRequiredFields') });
          return;
        }

        const payload = buildPaymentPayloadFromForm();
        await paymentService.update(editingId, payload);
        setFeedback({ variant: 'success', message: t('paymentUpdatedSuccessfully') });
      } else {
        if (draftPaymentsToAdd.length === 0) {
          setModalFeedback({ variant: 'danger', message: t('addPaymentToListBeforeSaving') });
          return;
        }

        for (const paymentToCreate of draftPaymentsToAdd) {
          await paymentService.create(paymentToCreate);
        }

        setFeedback({
          variant: 'success',
          message: draftPaymentsToAdd.length > 1 ? t('paymentsCreatedSuccessfully') : t('paymentCreatedSuccessfully'),
        });
      }
      handleCloseModal();
      await Promise.all([loadSelectedMonthPayments(), loadThreeMonthOverview()]);
    } catch (err) {
      setFeedback({ variant: 'danger', message: t('failedToSavePayment') });
      console.error(err);
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (id: number) => {
    setConfirmationConfig({
      title: t('deletePaymentTitle'),
      message: t('deletePaymentConfirmMessage'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          setConfirmLoading(true);
          setFeedback({ variant: 'info', message: t('deletingPayment') });
          await paymentService.delete(id);
          setFeedback({ variant: 'success', message: t('paymentDeletedSuccessfully') });
          setShowConfirmation(false);
          await Promise.all([loadSelectedMonthPayments(), loadThreeMonthOverview()]);
        } catch (err) {
          setFeedback({ variant: 'danger', message: t('failedToDeletePayment') });
          console.error(err);
        } finally {
          setConfirmLoading(false);
        }
      },
    });
    setShowConfirmation(true);
  };

  const filteredPayments = useMemo(() => {
    if (!filterAccountId) return payments;
    return payments.filter((payment) => payment.accountHolderId === filterAccountId);
  }, [payments, filterAccountId]);

  const selectedAccountHolderLabel = filterAccountId
    ? accountHolders.find((holder) => holder.id === filterAccountId)?.name ?? t('unknown')
    : t('allAccountHolders');

  const selectedMonthLabel = formatMonthYear(debouncedSelectedMonth);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterAccountId, debouncedSelectedMonth, pageSize]);

  const selectedAccountHolderMonthlyStats = useMemo(() => {
    if (!filterAccountId || isSavingsMode) {
      return null;
    }

    const selectedHolder = accountHolders.find((holder) => holder.id === filterAccountId);
    const monthlyAccountFees = (selectedHolder?.addressFees || []).length > 0
      ? (selectedHolder?.addressFees || []).reduce((sum, item) => sum + (item.monthlyFee || 0), 0)
      : (selectedHolder?.monthlyFee || 0);
    const holderCredits = (selectedHolder?.creditNotes || []).reduce((sum, note) => sum + Math.max(note.amount || 0, 0), 0);

    const nonSystemTransactions = filteredPayments.filter(
      (payment) => !resolveTransactionSystemCategory(payment, systemCategoryKeywordConfig)
    );

    return {
      transactionCount: nonSystemTransactions.length,
      totalAmount: nonSystemTransactions.reduce((sum, payment) => sum + payment.amount, 0),
      holderCredits,
      monthlyAccountFees,
      outstandingFees: Math.max(monthlyAccountFees - holderCredits, 0),
    };
  }, [filterAccountId, filteredPayments, isSavingsMode, systemCategoryKeywordConfig, accountHolders]);

  const accountHolderMonthlySummary = useMemo(() => {
    if (filterAccountId || isSavingsMode) {
      return [];
    }

    const summaryMap = new Map<number, AccountHolderSummaryRow>();

    filteredPayments.forEach((payment) => {
      const mappedSystemCategory = resolveTransactionSystemCategory(payment, systemCategoryKeywordConfig);
      if (mappedSystemCategory) {
        return;
      }

      const existing = summaryMap.get(payment.accountHolderId);
      if (existing) {
        existing.transactionCount += 1;
        existing.totalAmount += payment.amount;
        existing.outstandingFees = Math.max(existing.monthlyAccountFees - existing.holderCredits, 0);
      } else {
        const holder = accountHolders.find((item) => item.id === payment.accountHolderId);
        const monthlyAccountFees = (holder?.addressFees || []).length > 0
          ? (holder?.addressFees || []).reduce((sum, item) => sum + (item.monthlyFee || 0), 0)
          : (holder?.monthlyFee || 0);
        const holderCredits = (holder?.creditNotes || []).reduce((sum, note) => sum + Math.max(note.amount || 0, 0), 0);
        summaryMap.set(payment.accountHolderId, {
          accountHolderId: payment.accountHolderId,
          accountHolderName: payment.accountHolderName || t('unknown'),
          transactionCount: 1,
          totalAmount: payment.amount,
          holderCredits,
          monthlyAccountFees,
          outstandingFees: Math.max(monthlyAccountFees - holderCredits, 0),
        });
      }
    });

    return Array.from(summaryMap.values()).sort((a, b) => b.holderCredits - a.holderCredits);
  }, [filterAccountId, filteredPayments, isSavingsMode, systemCategoryKeywordConfig, t, accountHolders]);

  const bankFeeChargeSummary = useMemo(() => {
    const summaryMap = new Map<string, BankFeeChargeSummaryRow>();

    filteredPayments.forEach((payment) => {
      const category = resolveTransactionSystemCategory(payment, systemCategoryKeywordConfig);
      if (!category) {
        return;
      }

      const existing = summaryMap.get(category);
      if (existing) {
        existing.transactionCount += 1;
        existing.totalAmount += payment.amount;
      } else {
        summaryMap.set(category, {
          categoryName: category,
          transactionCount: 1,
          totalAmount: payment.amount,
        });
      }
    });

    return Array.from(summaryMap.values()).sort((a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount));
  }, [filteredPayments, systemCategoryKeywordConfig]);

  const selectedMonthKpis = useMemo(() => {
    const totalTransactions = filteredPayments.length;
    const totalAmount = filteredPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const totalIncoming = filteredPayments
      .filter((payment) => payment.amount > 0)
      .reduce((sum, payment) => sum + payment.amount, 0);
    const totalOutgoing = filteredPayments
      .filter((payment) => payment.amount < 0)
      .reduce((sum, payment) => sum + Math.abs(payment.amount), 0);
    const systemChargeTotal = bankFeeChargeSummary.reduce((sum, row) => sum + row.totalAmount, 0);

    return {
      totalTransactions,
      totalAmount,
      totalIncoming,
      totalOutgoing,
      systemChargeTotal,
    };
  }, [filteredPayments, bankFeeChargeSummary]);

  const lastThreeMonthsOverallStats = useMemo(() => {
    const months = Array.from({ length: 3 }, (_, index) => {
      const monthDate = new Date(debouncedSelectedMonth.getFullYear(), debouncedSelectedMonth.getMonth() - (2 - index), 1);
      return {
        key: toMonthKey(monthDate),
        label: formatMonthYear(monthDate),
      };
    });

    const monthlyStats = months.map((month) => {
      const monthPayments = lastThreeMonthsPayments.filter((payment) => toMonthKey(new Date(payment.paymentDate)) === month.key);

      const incomingAmount = monthPayments
        .filter((payment) => payment.amount > 0)
        .reduce((sum, payment) => sum + payment.amount, 0);

      const outgoingAmount = monthPayments
        .filter((payment) => payment.amount < 0)
        .reduce((sum, payment) => sum + Math.abs(payment.amount), 0);

      return {
        monthLabel: month.label,
        transactionCount: monthPayments.length,
        incomingAmount,
        outgoingAmount,
        totalAmount: monthPayments.reduce((sum, payment) => sum + payment.amount, 0),
      };
    });

    return {
      monthlyStats,
      totalTransactions: monthlyStats.reduce((sum, month) => sum + month.transactionCount, 0),
      totalAmount: monthlyStats.reduce((sum, month) => sum + month.totalAmount, 0),
    };
  }, [lastThreeMonthsPayments, debouncedSelectedMonth, formatMonthYear]);

  const isInterestTransaction = useCallback((payment: Payment) => {
    const description = (payment.description || '').toLowerCase();
    return description.includes('interest');
  }, []);

  const selectedMonthSavingsStats = useMemo(() => {
    const interestIncome = filteredPayments
      .filter((payment) => isInterestTransaction(payment) && payment.amount > 0)
      .reduce((sum, payment) => sum + payment.amount, 0);

    const systemCharges = filteredPayments
      .filter((payment) => !!resolveTransactionSystemCategory(payment, systemCategoryKeywordConfig))
      .reduce((sum, payment) => sum + Math.abs(payment.amount), 0);

    return {
      interestIncome,
      systemCharges,
      netAfterCharges: interestIncome - systemCharges,
    };
  }, [filteredPayments, isInterestTransaction, systemCategoryKeywordConfig]);

  const lastThreeMonthsSavingsInterestVsCharges = useMemo(() => {
    const months = Array.from({ length: 3 }, (_, index) => {
      const monthDate = new Date(debouncedSelectedMonth.getFullYear(), debouncedSelectedMonth.getMonth() - (2 - index), 1);
      return {
        key: toMonthKey(monthDate),
        label: formatMonthYear(monthDate),
      };
    });

    const monthlyStats = months.map((month) => {
      const monthPayments = lastThreeMonthsPayments.filter((payment) => toMonthKey(new Date(payment.paymentDate)) === month.key);

      const interestIncome = monthPayments
        .filter((payment) => isInterestTransaction(payment) && payment.amount > 0)
        .reduce((sum, payment) => sum + payment.amount, 0);

      const systemCharges = monthPayments
        .filter((payment) => !!resolveTransactionSystemCategory(payment, systemCategoryKeywordConfig))
        .reduce((sum, payment) => sum + Math.abs(payment.amount), 0);

      return {
        monthLabel: month.label,
        interestIncome,
        systemCharges,
        netAfterCharges: interestIncome - systemCharges,
      };
    });

    return {
      monthlyStats,
      totalInterestIncome: monthlyStats.reduce((sum, month) => sum + month.interestIncome, 0),
      totalSystemCharges: monthlyStats.reduce((sum, month) => sum + month.systemCharges, 0),
      totalNetAfterCharges: monthlyStats.reduce((sum, month) => sum + month.netAfterCharges, 0),
    };
  }, [lastThreeMonthsPayments, debouncedSelectedMonth, formatMonthYear, isInterestTransaction, systemCategoryKeywordConfig]);

  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pagedPayments = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * pageSize;
    return filteredPayments.slice(startIndex, startIndex + pageSize);
  }, [filteredPayments, safeCurrentPage, pageSize]);

  const pageStart = filteredPayments.length === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(safeCurrentPage * pageSize, filteredPayments.length);

  if (loading && !hasLoadedOnce) {
    return (
      <Container className="py-5 page-loading-shell">
        <div className="page-loading-header">
          <div className="page-loading-title page-skeleton-shimmer" aria-hidden="true" />
          <div className="page-loading-subtitle">
            <LoadingIndicator label={t('loading')} className="system-loading-status-card-inline" showLines={false} />
          </div>
        </div>
        <div className="row g-4">
          <div className="col-lg-4 col-md-6">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-lg-8 col-md-6">
            <div className="page-loading-block page-loading-block-tall page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-12">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-5 payments-page" aria-busy={loading || savingPayment || confirmLoading}>
      <div className="payments-hero page-header-panel">
        <div className="payments-header">
          <div className="payments-header-copy">
            <h1 className="mb-0 page-header-title">
              <span className="page-header-title-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                  <rect x="3.5" y="5.5" width="17" height="13" rx="2.6" fill="none" stroke="currentColor" strokeWidth="1.9" />
                  <path d="M3.8 10h16.4" fill="none" stroke="currentColor" strokeWidth="1.9" />
                  <path d="M7 14.2h3.6M13.4 14.2h3.6" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                </svg>
              </span>
              {t('accountOverviewTitle')}
            </h1>
            <div className="payments-active-account-pill">
              <span className="payments-active-account-label">{t('activeAccount')}</span>
              <span className="payments-active-account-value">
                {selectedTrustAccount?.accountName || selectedTrustAccountTypeLabel}
                <span className="payments-active-account-type">{selectedTrustAccountTypeLabel}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="payments-actions-strip mb-4">
        <div className="d-flex align-items-center gap-2 payments-header-actions-strip">
          <Button
            variant="light"
            className="payments-action-btn payments-action-btn-icon"
            onClick={() => setShowAccountSwitchModal(true)}
            aria-label={t('switchAccounts')}
            title={t('switchAccounts')}
            disabled={loadingTrustAccounts || trustAccounts.length < 2}
          >
            <span className="payments-action-btn-label">{t('switchAccounts')}</span>
            <svg viewBox="0 0 24 24" className="payments-action-btn-icon-glyph" focusable="false" aria-hidden="true">
              <path d="M7 7h10.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              <path d="M14.5 4l3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17 17H6.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              <path d="M9.5 14l-3 3 3 3" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Button>
          {isCurrentAccountMode && (
            <Button
              variant="light"
              className="payments-action-btn payments-action-btn-icon"
              onClick={() => handleShowModal()}
              aria-label={t('addNewPayments')}
              title={t('addNewPayments')}
            >
              <span className="payments-action-btn-label">{t('addNewPayments')}</span>
              <svg viewBox="0 0 24 24" className="payments-action-btn-icon-glyph" focusable="false" aria-hidden="true">
                <circle cx="9" cy="7.2" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.9" />
                <path d="M3.3 16.4a5.9 5.9 0 0 1 11.8 0" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                <circle cx="18" cy="17.1" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.9" />
                <path d="M18 15.5v3.2M16.4 17.1h3.2" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              </svg>
            </Button>
          )}
          <Button
            variant="light"
            className="payments-action-btn payments-action-btn-icon"
            onClick={() => setShowStatementsCenterModal(true)}
            aria-label={t('statements')}
            title={t('statements')}
          >
            <span className="payments-action-btn-label">{t('statements')}</span>
            <svg viewBox="0 0 24 24" className="payments-action-btn-icon-glyph" focusable="false" aria-hidden="true">
              <path d="M6 3.5h9.2a2.8 2.8 0 0 1 2.8 2.8v11.4a2.8 2.8 0 0 1-2.8 2.8H6a2.8 2.8 0 0 1-2.8-2.8V6.3A2.8 2.8 0 0 1 6 3.5z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M7.3 8.1h6.7M7.3 11.6h8.4M7.3 15.1h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M16.1 15.7l2.5 2.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {feedback && (
        <Alert variant={feedback.variant} onClose={() => setFeedback(null)} dismissible>
          {feedback.message}
        </Alert>
      )}

      <Card className="payments-filter-card dashboard-card dashboard-filter-panel mb-4">
        <Card.Body>
          <div className="row g-3 align-items-end">
            {!isSavingsMode && (
              <div className="col-lg-5 col-md-6">
                <Form.Label>{t('accountHolder')}</Form.Label>
                <div className="app-dropdown-arrow-wrapper">
                  <Form.Select
                    value={filterAccountId || ''}
                    onChange={(e) => setFilterAccountId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  >
                    <option value="">{t('allAccountHolders')}</option>
                    {accountHolders.map((holder) => (
                      <option key={holder.id} value={holder.id}>
                        {holder.name}
                      </option>
                    ))}
                  </Form.Select>
                  <DropdownArrow />
                </div>
              </div>
            )}
            <div className={isSavingsMode ? 'col-lg-9 col-md-8 payments-month-filter-col' : 'col-lg-4 col-md-4 payments-month-filter-col'}>
              <Form.Label>{t('date')}</Form.Label>
              <div className="app-date-picker-shell payments-month-filter-shell">
                <DatePicker
                  selected={selectedMonth}
                  onChange={(date: Date | null) => date && setSelectedMonth(date)}
                  onChangeRaw={(e) => e.preventDefault()}
                  onKeyDown={(e) => e.preventDefault()}
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  showFullMonthYearPicker
                  locale={language === 'af' ? 'af' : 'en-GB'}
                  className="form-control app-month-picker"
                  placeholderText={t('selectMonth')}
                />
                <span className="app-date-picker-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                    <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
              </div>
            </div>
            <div className={`${isSavingsMode ? 'col-lg-3 col-md-4' : 'col-lg-3 col-md-2'} payments-filter-reset-col`}>
              <Button
                variant="outline-secondary"
                size="sm"
                className="dashboard-wire-action-btn dashboard-recent-utility-btn dashboard-recent-reset-btn"
                onClick={() => {
                  const previousMonth = getPreviousMonth();
                  setFilterAccountId(undefined);
                  setSelectedMonth(previousMonth);
                }}
              >
                {t('reset')}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>



      {filteredPayments.length === 0 && !loading ? (
        <Alert variant="info" className="payments-empty-alert">{t('noPaymentsFound')}</Alert>
      ) : (
        <Card className="payments-summary-card dashboard-card d-flex flex-column mb-4">
          <Card.Body className="d-flex flex-column">
            <div className="d-flex justify-content-between align-items-center mb-3 payments-grid-section-header">
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 payments-section-title">{t('transactionSummary')}</h5>
                <span className="payments-section-count">{filteredPayments.length}</span>
              </div>
              <div className="payments-table-month-label text-end" style={{ minWidth: 120 }}>
                <span className="badge bg-light text-dark px-3 py-2" style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {formatMonthYear(selectedMonth)}
                </span>
              </div>
            </div>

            <div className="table-responsive scrollable-table payments-table-wrapper dashboard-wire-table-wrap flex-grow-1 mb-3">
              <Table className="payments-data-table dashboard-table">
                <thead>
                  <tr>
                    <th>{t('date')}</th>
                    <th>{t('description')}</th>
                    <th>{t('accountHolder')}</th>
                    <th className="text-end">{t('amount')}</th>
                    <th className="text-center">{t('actions')}</th>
                    <th className="payments-edit-column-header" aria-label={t('edit')}></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        <div className="dashboard-grid-loading-message">
                          <LoadingIndicator label={t('loading')} subtle />
                        </div>
                      </td>
                    </tr>
                  ) : pagedPayments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{formatDate(payment.paymentDate)}</td>
                      <td className="text-truncate" title={payment.description}>{payment.description}</td>
                      <td>{payment.accountHolderName}</td>
                      <td className={`text-end ${payment.amount >= 0 ? 'amount-positive' : 'amount-negative'}`}>{formatCurrency(payment.amount)}</td>
                      <td className="standard-grid-action-cell">
                        <div className="standard-grid-action-wrap">
                          <Button
                            variant="danger"
                            size="sm"
                            className="payments-action-btn payments-action-delete"
                            onClick={() => handleDeletePayment(payment.id)}
                          >
                            {t('delete')}
                          </Button>
                        </div>
                      </td>
                      <td className="payments-edit-column-cell">
                        <div className="standard-grid-action-wrap">
                          <Button
                            variant="warning"
                            size="sm"
                            className="payments-edit-btn"
                            onClick={() => handleShowModal(payment)}
                            aria-label={t('edit')}
                            title={t('edit')}
                          >
                            <svg viewBox="0 0 24 24" className="payments-edit-btn-icon" focusable="false" aria-hidden="true">
                              <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                              <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                              <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="dashboard-table-footer d-flex justify-content-between align-items-center payments-table-meta">
              <div className="dashboard-table-meta text-muted payments-table-meta-text">
                {t('showing')} {pageStart}-{pageEnd} {t('of')} {filteredPayments.length}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                <div className="app-dropdown-arrow-wrapper" style={{ width: '90px' }}>
                  <Form.Select
                    className="dashboard-rows-select"
                    value={pageSize}
                    onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                    disabled={loading}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                  <DropdownArrow />
                </div>
                <Pagination className="mb-0 dashboard-pagination">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1 || loading} />
                  <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safeCurrentPage === 1 || loading} />
                  <Pagination.Item active>{safeCurrentPage}</Pagination.Item>
                  {totalPages > 1 && <Pagination.Item disabled>/ {totalPages}</Pagination.Item>}
                  <Pagination.Next
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={safeCurrentPage === totalPages || loading}
                  />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage === totalPages || loading} />
                </Pagination>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Transaction Summary Panel: KPI Cards and summaries moved below the table */}
      <Row className="g-4 mb-4">
        <Col xs={12}>
          <div className={`payments-dependent-panel ${loading ? 'is-updating' : ''}`}> 
            <div className="payments-kpi-grid payments-dependent-content dashboard-dependent-content">
              <Card className="payments-kpi-card dashboard-card payments-kpi-card-transactions">
                <Card.Body>
                  <div className="payments-kpi-label">{t('transactions')}</div>
                  <div className="payments-kpi-value">{selectedMonthKpis.totalTransactions}</div>
                  <div className="payments-kpi-caption">{selectedMonthLabel}</div>
                </Card.Body>
              </Card>

              <Card className={`payments-kpi-card dashboard-card ${selectedMonthKpis.totalAmount >= 0 ? 'payments-kpi-card-incoming' : 'payments-kpi-card-outgoing'}`}> 
                <Card.Body>
                  <div className="payments-kpi-label">{t('totalAmount')}</div>
                  <div className="payments-kpi-value">{formatCurrency(selectedMonthKpis.totalAmount)}</div>
                  <div className="payments-kpi-caption">{selectedMonthLabel}</div>
                  <div className="payments-kpi-breakdown mt-2">
                    <div className="payments-kpi-breakdown-row">
                      <span>{t('incoming')}</span>
                      <span className="payments-amount-positive">{formatCurrency(selectedMonthKpis.totalIncoming)}</span>
                    </div>
                    <div className="payments-kpi-breakdown-row">
                      <span>{t('outgoing')}</span>
                      <span className="payments-amount-negative">{formatCurrency(selectedMonthKpis.totalOutgoing)}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="payments-kpi-card dashboard-card payments-kpi-card-fees">
                <Card.Body>
                  <div className="payments-kpi-label">{t('systemFeeChargeLabel')}</div>
                  <div className="payments-kpi-value">{formatCurrency(selectedMonthKpis.systemChargeTotal)}</div>
                  <div className="payments-kpi-caption">{selectedMonthLabel}</div>
                </Card.Body>
              </Card>
            </div>
            {loading && (
              <div className="payments-dependent-overlay dashboard-dependent-overlay">
                <LoadingIndicator label={t('loading')} subtle />
              </div>
            )}
          </div>
        </Col>

        <Col xs={12}>
          <Card className={`payments-summary-card dashboard-card dashboard-card-dependent payments-dependent-card ${loading ? 'is-updating' : ''}`}>
            <Card.Body>
              <div className="payments-dependent-content dashboard-dependent-content">
                <div className="payments-summary-header">
                  <h5 className="mb-1">{isSavingsMode ? t('savingsInterestVsSystemFeesLastThreeMonths') : t('overallLastThreeMonths')}</h5>
                </div>
                <div className="table-responsive payments-summary-table-wrapper dashboard-wire-table-wrap">
                  <Table size="sm" className="payments-summary-table dashboard-table mb-0">
              <thead>
                <tr>
                  <th>{t('month')}</th>
                  {isSavingsMode ? (
                    <>
                      <th className="text-end">{t('interestIncome')}</th>
                      <th className="text-end">{t('systemFeeChargeLabel')}</th>
                      <th className="text-end">{t('netAfterSystemCharges')}</th>
                    </>
                  ) : (
                    <>
                      <th className="text-end">{t('transactions')}</th>
                      <th className="text-end">{t('incoming')}</th>
                      <th className="text-end">{t('outgoing')}</th>
                      <th className="text-end">{t('totalAmount')}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {isSavingsMode ? (
                  <>
                    {lastThreeMonthsSavingsInterestVsCharges.monthlyStats.map((month) => (
                      <tr key={month.monthLabel}>
                        <td>{month.monthLabel}</td>
                        <td className="text-end payments-amount-positive payments-month-row-value">{formatCurrency(month.interestIncome)}</td>
                        <td className="text-end payments-amount-negative payments-month-row-value">{formatCurrency(month.systemCharges)}</td>
                        <td className={`text-end payments-month-row-value ${month.netAfterCharges >= 0 ? 'payments-amount-positive' : 'payments-amount-negative'}`}>{formatCurrency(month.netAfterCharges)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td><strong>{t('totalThreeMonths')}</strong></td>
                      <td className="text-end payments-amount-positive"><strong>{formatCurrency(lastThreeMonthsSavingsInterestVsCharges.totalInterestIncome)}</strong></td>
                      <td className="text-end payments-amount-negative"><strong>{formatCurrency(lastThreeMonthsSavingsInterestVsCharges.totalSystemCharges)}</strong></td>
                      <td className={`text-end ${lastThreeMonthsSavingsInterestVsCharges.totalNetAfterCharges >= 0 ? 'payments-amount-positive' : 'payments-amount-negative'}`}><strong>{formatCurrency(lastThreeMonthsSavingsInterestVsCharges.totalNetAfterCharges)}</strong></td>
                    </tr>
                  </>
                ) : (
                  <>
                    {lastThreeMonthsOverallStats.monthlyStats.map((month) => (
                      <tr key={month.monthLabel}>
                        <td>{month.monthLabel}</td>
                        <td className="text-end payments-month-row-value">{month.transactionCount}</td>
                        <td className="text-end payments-amount-positive payments-month-row-value">{formatCurrency(month.incomingAmount)}</td>
                        <td className="text-end payments-amount-negative payments-month-row-value">{formatCurrency(month.outgoingAmount)}</td>
                        <td className={`text-end payments-month-row-value ${month.totalAmount >= 0 ? 'payments-amount-positive' : 'payments-amount-negative'}`}>{formatCurrency(month.totalAmount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td><strong>{t('totalThreeMonths')}</strong></td>
                      <td className="text-end"><strong>{lastThreeMonthsOverallStats.totalTransactions}</strong></td>
                      <td className="text-end payments-amount-positive"><strong>{formatCurrency(lastThreeMonthsOverallStats.monthlyStats.reduce((sum, month) => sum + month.incomingAmount, 0))}</strong></td>
                      <td className="text-end payments-amount-negative"><strong>{formatCurrency(lastThreeMonthsOverallStats.monthlyStats.reduce((sum, month) => sum + month.outgoingAmount, 0))}</strong></td>
                      <td className={`text-end ${lastThreeMonthsOverallStats.totalAmount >= 0 ? 'payments-amount-positive' : 'payments-amount-negative'}`}><strong>{formatCurrency(lastThreeMonthsOverallStats.totalAmount)}</strong></td>
                    </tr>
                  </>
                )}
              </tbody>
                  </Table>
                </div>
              </div>
              {loading && (
                <div className="payments-dependent-overlay dashboard-dependent-overlay">
                  <LoadingIndicator label={t('loading')} subtle />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7} xs={12}>
          <Card className={`payments-summary-card dashboard-card dashboard-card-dependent payments-dependent-card h-100 ${loading ? 'is-updating' : ''}`}>
            <Card.Body>
              <div className="payments-dependent-content dashboard-dependent-content">
                <div className="payments-summary-header">
                  <h5 className="mb-1">{isSavingsMode ? t('savingsAccountStats') : t('accountHolderSummary')}</h5>
                  <span className="payments-summary-subtitle">{selectedMonthLabel}</span>
                </div>

                {isSavingsMode && (
                  <div className="table-responsive payments-summary-table-wrapper dashboard-wire-table-wrap">
                    <Table size="sm" className="payments-summary-table dashboard-table mb-0">
                      <thead>
                        <tr>
                          <th>{t('category')}</th>
                          <th className="text-end">{t('totalAmount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{t('interestIncome')}</td>
                          <td className="text-end payments-amount-positive">{formatCurrency(selectedMonthSavingsStats.interestIncome)}</td>
                        </tr>
                        <tr>
                          <td>{t('systemFeeChargeLabel')}</td>
                          <td className="text-end payments-amount-negative">{formatCurrency(selectedMonthSavingsStats.systemCharges)}</td>
                        </tr>
                        <tr>
                          <td><strong>{t('netAfterSystemCharges')}</strong></td>
                          <td className={`text-end ${selectedMonthSavingsStats.netAfterCharges >= 0 ? 'payments-amount-positive' : 'payments-amount-negative'}`}>
                            <strong>{formatCurrency(selectedMonthSavingsStats.netAfterCharges)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}

                {!isSavingsMode && filterAccountId && selectedAccountHolderMonthlyStats ? (
                  <div className="table-responsive payments-summary-table-wrapper dashboard-wire-table-wrap">
                    <Table size="sm" className="payments-summary-table dashboard-table mb-0">
                <thead>
                  <tr>
                    <th>{t('accountHolder')}</th>
                    <th className="text-end">{t('transactions')}</th>
                    <th className="text-end">{t('notes')}</th>
                    <th className="text-end">{t('outstandingFees')}</th>
                    <th className="text-end">{t('totalAmount')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{selectedAccountHolderLabel}</td>
                    <td className="text-end">{selectedAccountHolderMonthlyStats.transactionCount}</td>
                    <td className="text-end">{formatCurrency(selectedAccountHolderMonthlyStats.holderCredits)}</td>
                    <td className={`text-end ${selectedAccountHolderMonthlyStats.outstandingFees > 0 ? 'payments-amount-negative' : 'payments-amount-positive'}`}>{formatCurrency(selectedAccountHolderMonthlyStats.outstandingFees)}</td>
                    <td className={`text-end ${selectedAccountHolderMonthlyStats.totalAmount >= 0 ? 'payments-amount-positive' : 'payments-amount-negative'}`}>{formatCurrency(selectedAccountHolderMonthlyStats.totalAmount)}</td>
                  </tr>
                </tbody>
                    </Table>
                  </div>
                ) : null}

                {!isSavingsMode && !filterAccountId && accountHolderMonthlySummary.length > 0 ? (
                  <div className="table-responsive payments-summary-table-wrapper dashboard-wire-table-wrap">
                    <Table size="sm" className="payments-summary-table dashboard-table mb-0">
                <thead>
                  <tr>
                    <th>{t('accountHolder')}</th>
                    <th className="text-end">{t('transactions')}</th>
                    <th className="text-end">{t('notes')}</th>
                    <th className="text-end">{t('outstandingFees')}</th>
                    <th className="text-end">{t('totalAmount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {accountHolderMonthlySummary.map((summary) => (
                    <tr key={summary.accountHolderId}>
                      <td>{summary.accountHolderName}</td>
                      <td className="text-end">{summary.transactionCount}</td>
                      <td className="text-end">{formatCurrency(summary.holderCredits)}</td>
                      <td className={`text-end ${summary.outstandingFees > 0 ? 'payments-amount-negative' : 'payments-amount-positive'}`}>{formatCurrency(summary.outstandingFees)}</td>
                      <td className={`text-end ${summary.totalAmount >= 0 ? 'payments-amount-positive' : 'payments-amount-negative'}`}>{formatCurrency(summary.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
                    </Table>
                  </div>
                ) : null}

                {!isSavingsMode && ((filterAccountId && (selectedAccountHolderMonthlyStats?.transactionCount ?? 0) === 0) ||
                  (!filterAccountId && accountHolderMonthlySummary.length === 0)) && (
                  <div className="payments-summary-empty">{t('noAccountHolderSummaryForFilters')}</div>
                )}
              </div>
              {loading && (
                  <div className="payments-dependent-overlay dashboard-dependent-overlay">
                    <LoadingIndicator label={t('loading')} subtle />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5} xs={12}>
          <Card className={`payments-summary-card dashboard-card dashboard-card-dependent payments-dependent-card h-100 ${loading ? 'is-updating' : ''}`}>
            <Card.Body>
              <div className="payments-dependent-content dashboard-dependent-content">
                <div className="payments-summary-header">
                  <h5 className="mb-1">{t('systemFeesChargesOverview')}</h5>
                  <span className="payments-summary-subtitle">{selectedMonthLabel}</span>
                </div>
                {bankFeeChargeSummary.length > 0 ? (
                  <div className="table-responsive payments-summary-table-wrapper dashboard-wire-table-wrap">
                    <Table size="sm" className="payments-summary-table dashboard-table mb-0">
                      <thead>
                        <tr>
                          <th>{t('systemFeeChargeLabel')}</th>
                          <th className="text-end">{t('transactions')}</th>
                          <th className="text-end">{t('totalAmount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankFeeChargeSummary.map((summary) => (
                          <tr key={summary.categoryName}>
                            <td><span className="payments-category-badge">{summary.categoryName}</span></td>
                            <td className="text-end">{summary.transactionCount}</td>
                            <td className={`text-end ${summary.totalAmount >= 0 ? 'payments-amount-positive' : 'payments-amount-negative'}`}>{formatCurrency(summary.totalAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="payments-summary-empty">{t('noSystemFeeChargeCategoriesForFilters')}</div>
                )}
              </div>
              {loading && (
                  <div className="payments-dependent-overlay dashboard-dependent-overlay">
                    <LoadingIndicator label={t('loading')} subtle />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showStatementsCenterModal}
        onHide={() => setShowStatementsCenterModal(false)}
        size="xl"
        centered
        dialogClassName="payments-statements-center-dialog"
        className="payments-statements-center-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('statementsTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="payments-statements-center-body">
            <Statements compact />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowStatementsCenterModal(false)}>
            {t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAccountSwitchModal} onHide={() => setShowAccountSwitchModal(false)} className="payments-modal payments-account-switch-modal" centered>
        <Modal.Header closeButton={!loadingTrustAccounts}>
          <Modal.Title>{t('switchAccounts')}</Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={loadingTrustAccounts}>
          <div className="payments-account-switch-copy">{t('switchAccountsPrompt')}</div>
          {loadingTrustAccounts ? (
            <LoadingIndicator label={t('loading')} />
          ) : trustAccounts.length === 0 ? (
            <Alert variant="info" className="mb-0">{t('noTrustAccountsConfigured')}</Alert>
          ) : (
            <div className="payments-account-switch-list">
              {trustAccounts.map((account) => {
                const accountTypeLabel = (account.accountKind || '').trim().toLowerCase() === 'savings'
                  ? t('savingsAccountType')
                  : t('currentAccountType');
                const isSelected = account.id === selectedTrustAccountId;

                return (
                  <Button
                    key={account.id}
                    variant={isSelected ? 'primary' : 'outline-secondary'}
                    className={`payments-account-switch-option${isSelected ? ' is-selected' : ''}`}
                    onClick={() => handleSwitchAccount(account)}
                  >
                    <span className="payments-account-switch-option-main">
                      <span className="payments-account-switch-option-title">{account.accountName}</span>
                      <span className="payments-account-switch-option-meta">
                        {accountTypeLabel} · {account.accountNumber}
                      </span>
                    </span>
                    <span className="payments-account-switch-option-status">
                      {isSelected ? t('active') : accountTypeLabel}
                    </span>
                  </Button>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAccountSwitchModal(false)}>
            {t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModal} onHide={handleRequestCloseModal} className="payments-modal" dialogClassName="payments-modal-dialog" centered>
        <Modal.Header closeButton={!savingPayment}>
          <Modal.Title>
            {editingId ? `${t('edit')} ${t('payments')}` : t('addNewPayments')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="payments-modal-body" aria-busy={savingPayment}>
          {modalFeedback && (
            <Alert variant={modalFeedback.variant} onClose={() => setModalFeedback(null)} dismissible>
              {modalFeedback.message}
            </Alert>
          )}
          <Form>
            {editingId ? (
              <div className="payments-modal-section">
                <div className="payments-modal-section-title">{t('paymentsTitle')}</div>
                <div className="payments-modal-form-grid">
                  <Form.Group className="mb-0">
                    <Form.Label>{t('accountHolder')}</Form.Label>
                    <div className="app-dropdown-arrow-wrapper">
                      <Form.Select
                        name="accountHolderId"
                        value={formData.accountHolderId}
                        onChange={handleFormChange}
                        onBlur={() => setPaymentFormValidated(true)}
                        isInvalid={paymentFormValidated && formData.accountHolderId <= 0}
                      >
                        <option value="">{t('accountHolder')}</option>
                        {accountHolders.map((holder) => (
                          <option key={holder.id} value={holder.id}>
                            {holder.name}
                          </option>
                        ))}
                      </Form.Select>
                      <DropdownArrow />
                    </div>
                    <Form.Control.Feedback type="invalid">{t('pleaseSelectAccountHolder')}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-0">
                    <Form.Label>{t('amount')}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      name="amount"
                      value={formData.amount === 0 ? '' : formData.amount}
                      onChange={handleFormChange}
                      onBlur={() => setPaymentFormValidated(true)}
                      isInvalid={paymentFormValidated && formData.amount === 0}
                      className="form-control-lg payments-amount-input"
                    />
                    <Form.Control.Feedback type="invalid">{t('paymentAmountMustBeGreaterThanZero')}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-0">
                    <Form.Label>{t('paymentDate')}</Form.Label>
                    <div className={`app-date-picker-shell${paymentFormValidated && formData.paymentDate.trim().length === 0 ? ' is-invalid' : ''}`}>
                      <DatePicker
                        selected={parseLocalDate(formData.paymentDate)}
                        onChange={handlePaymentDateChange}
                        onChangeRaw={(e) => e.preventDefault()}
                        onKeyDown={(e) => e.preventDefault()}
                        dateFormat="dd/MM/yyyy"
                        className={`form-control payments-date-picker${paymentFormValidated && formData.paymentDate.trim().length === 0 ? ' is-invalid' : ''}`}
                        placeholderText={t('selectDatePlaceholder')}
                        locale={language === 'af' ? 'af' : 'en-GB'}
                        popperPlacement="bottom-start"
                        popperClassName="payments-modal-datepicker-popper"
                      />
                      <span className="app-date-picker-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                          <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                    </div>
                    {paymentFormValidated && formData.paymentDate.trim().length === 0 && (
                      <div className="invalid-feedback d-block">{t('fieldRequired')}</div>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-0 payments-modal-description-field">
                    <Form.Label>{t('description')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      onBlur={() => setPaymentFormValidated(true)}
                      isInvalid={paymentFormValidated && formData.description.trim().length === 0}
                    />
                    <Form.Control.Feedback type="invalid">{t('fieldRequired')}</Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>
            ) : (
              <div className="payments-modal-section">
                <div className="payments-batch-helper">{t('addPaymentToListBeforeSaving')}</div>
                <div className="payments-batch-inline-grid">
                  <Form.Group className={`mb-0 payments-batch-field ${isBatchAccountHolderInvalid ? 'is-invalid' : ''}`}>
                    <Form.Label>{t('accountHolder')}</Form.Label>
                    <div className="app-dropdown-arrow-wrapper">
                      <Form.Select
                        name="accountHolderId"
                        value={formData.accountHolderId}
                        onChange={handleFormChange}
                        isInvalid={isBatchAccountHolderInvalid}
                      >
                        <option value="">{t('accountHolder')}</option>
                        {accountHolders.map((holder) => (
                          <option key={holder.id} value={holder.id}>
                            {holder.name}
                          </option>
                        ))}
                      </Form.Select>
                      <DropdownArrow />
                    </div>
                    <Form.Control.Feedback type="invalid">{t('pleaseSelectAccountHolder')}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className={`mb-0 payments-batch-field ${isBatchAmountInvalid ? 'is-invalid' : ''}`}>
                    <Form.Label>{t('amount')}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      name="amount"
                      value={formData.amount === 0 ? '' : formData.amount}
                      onChange={handleFormChange}
                      isInvalid={isBatchAmountInvalid}
                      className="payments-amount-input"
                    />
                    <Form.Control.Feedback type="invalid">{t('paymentAmountMustBeGreaterThanZero')}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className={`mb-0 payments-batch-field ${isBatchPaymentDateInvalid ? 'is-invalid' : ''}`}>
                    <Form.Label>{t('paymentDate')}</Form.Label>
                    <div className={`app-date-picker-shell${isBatchPaymentDateInvalid ? ' is-invalid' : ''}`}>
                      <DatePicker
                        selected={parseLocalDate(formData.paymentDate)}
                        onChange={handlePaymentDateChange}
                        onChangeRaw={(e) => e.preventDefault()}
                        onKeyDown={(e) => e.preventDefault()}
                        dateFormat="dd/MM/yyyy"
                        className={`form-control payments-date-picker${isBatchPaymentDateInvalid ? ' is-invalid' : ''}`}
                        placeholderText={t('selectDatePlaceholder')}
                        locale={language === 'af' ? 'af' : 'en-GB'}
                        popperPlacement="bottom-start"
                        popperClassName="payments-modal-datepicker-popper"
                      />
                      <span className="app-date-picker-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                          <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                    </div>
                    <Form.Control.Feedback type="invalid">{t('fieldRequired')}</Form.Control.Feedback>
                  </Form.Group>
                </div>

                <Form.Group className={`mb-0 mt-2 payments-batch-field ${isBatchDescriptionInvalid ? 'is-invalid' : ''}`}>
                  <Form.Label>{t('description')}</Form.Label>
                  <div className="payments-batch-description-row">
                    <Form.Control
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      onKeyDown={handleDraftPaymentKeyDown}
                      isInvalid={isBatchDescriptionInvalid}
                    />
                    <Button type="button" variant="outline-primary" onClick={handleAddDraftPayment} className="payments-batch-add-btn">
                      + {t('add')}
                    </Button>
                  </div>
                  <Form.Control.Feedback type="invalid">{t('fieldRequired')}</Form.Control.Feedback>
                </Form.Group>

                <div className="payments-batch-actions">
                  {draftPaymentsToAdd.length > 0 && (
                    <Button type="button" variant="outline-danger" onClick={handleClearDraftPayments}>
                      {t('clearAll')}
                    </Button>
                  )}
                </div>

                {draftPaymentsToAdd.length > 0 && (() => {
                  const draftTotalPages = Math.max(1, Math.ceil(draftPaymentsToAdd.length / modalDraftPageSize));
                  const safeDraftPage = Math.min(modalDraftPage, draftTotalPages);
                  const draftPageStart = (safeDraftPage - 1) * modalDraftPageSize;
                  const pagedDraftPayments = draftPaymentsToAdd.slice(draftPageStart, draftPageStart + modalDraftPageSize);
                  const draftPageDisplayStart = draftPageStart + 1;
                  const draftPageDisplayEnd = Math.min(safeDraftPage * modalDraftPageSize, draftPaymentsToAdd.length);
                  return (
                    <div className="payments-batch-list">
                      <div className="payments-batch-list-title">{t('paymentsTitle')}</div>
                      {pagedDraftPayments.map((draftPayment, pageIdx) => {
                        const idx = draftPageStart + pageIdx;
                        const holderName = accountHolders.find((holder) => holder.id === draftPayment.accountHolderId)?.name || t('unknown');
                        return (
                          <div key={`${draftPayment.accountHolderId}-${draftPayment.paymentDate}-${idx}`} className="payments-batch-item">
                            <div className="payments-batch-item-main">
                              <span className="payments-batch-item-title">{holderName}</span>
                              <span className="payments-batch-item-meta">{formatDate(draftPayment.paymentDate)} - {formatCurrency(draftPayment.amount)}</span>
                              <span className="payments-batch-item-desc">{draftPayment.description}</span>
                            </div>
                            <button
                              type="button"
                              className="payments-batch-remove"
                              onClick={() => handleRemoveDraftPayment(idx)}
                              title={t('remove')}
                              aria-label={t('remove')}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                      <div className="dashboard-table-footer d-flex justify-content-between align-items-center payments-batch-list-footer">
                        <div className="dashboard-table-meta text-muted payments-table-meta-text">
                          {t('showing')} {draftPageDisplayStart}-{draftPageDisplayEnd} {t('of')} {draftPaymentsToAdd.length}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                          <div className="app-dropdown-arrow-wrapper" style={{ width: '90px' }}>
                            <Form.Select
                              className="dashboard-rows-select"
                              value={modalDraftPageSize}
                              onChange={(e) => {
                                setModalDraftPageSize(parseInt(e.target.value, 10));
                                setModalDraftPage(1);
                              }}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </Form.Select>
                            <DropdownArrow />
                          </div>
                          <Pagination className="mb-0 dashboard-pagination">
                            <Pagination.First onClick={() => setModalDraftPage(1)} disabled={safeDraftPage === 1} />
                            <Pagination.Prev onClick={() => setModalDraftPage((p) => Math.max(1, p - 1))} disabled={safeDraftPage === 1} />
                            <Pagination.Item active>{safeDraftPage}</Pagination.Item>
                            {draftTotalPages > 1 && <Pagination.Item disabled>/ {draftTotalPages}</Pagination.Item>}
                            <Pagination.Next
                              onClick={() => setModalDraftPage((p) => Math.min(draftTotalPages, p + 1))}
                              disabled={safeDraftPage === draftTotalPages}
                            />
                            <Pagination.Last onClick={() => setModalDraftPage(draftTotalPages)} disabled={safeDraftPage === draftTotalPages} />
                          </Pagination>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleRequestCloseModal} disabled={savingPayment}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSavePayment}
            className="payments-primary-action d-inline-flex align-items-center gap-2"
            disabled={
              savingPayment
              || (editingId
                ? !isPaymentFormValid()
                : draftPaymentsToAdd.length === 0)
            }
          >
            {savingPayment && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
            {savingPayment
              ? t('savingAction')
              : editingId
                ? t('update')
                : `${t('add')} (${draftPaymentsToAdd.length})`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      {confirmationConfig && (
        <ConfirmationModal
          show={showConfirmation}
          title={confirmationConfig.title}
          message={confirmationConfig.message}
          onConfirm={() => {
            confirmationConfig.onConfirm();
          }}
          onCancel={() => {
            setShowConfirmation(false);
            setConfirmationConfig(null);
          }}
          variant={confirmationConfig.variant || 'danger'}
          confirmText={confirmationConfig.confirmText || t('delete')}
          cancelText={confirmationConfig.cancelText}
          isLoading={confirmLoading}
        />
      )}
    </Container>
  );
};

export default Payments;

